import { createFileRoute, Link } from "@tanstack/react-router";
import { siteConfig } from "@/config/site";
import { pageMeta, ldScripts, breadcrumbLd, faqLd } from "@/lib/seo";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { SectionHeading } from "@/components/ui-ext/SectionHeading";

// Pull FAQs from various sections
const CONSOLIDATED_FAQS = [
  {
    q: "Do you guarantee Google rankings?",
    a: "No credible agency does. Google's algorithm is a black box that changes daily. What we guarantee is a documented engineering process — technical fixes, content sprints, and authority building — that reliably produces the signals Google and AI answer engines reward.",
  },
  {
    q: "How long before we see results?",
    a: "Technical remediation typically produces movement in 4–8 weeks. Local Pack presence compounds around month 3. Full organic growth engines take 6–12 months to reach full maturity, depending on your starting authority and competitive set.",
  },
  {
    q: "What does SEO cost at Optimize Plus?",
    a: "Flagship programs in competitive metros start at $6,000/mo. Corridor and single-city programs for smaller markets start at $2,000/mo. We scope based on the work needed to win, not a flat card. See our pricing page for details.",
  },
  {
    q: "What is the difference between SEO and AI search (GEO/AEO)?",
    a: "SEO is for ranking in the classic blue links. AEO/GEO is for earning citations in AI Overviews, ChatGPT, and Perplexity. They share a technical foundation but require different content structures and entity signals.",
  },
  {
    q: "Will we own the work if we stop?",
    a: "Yes. Every audit, every brief, every link earned, and every line of code we write for your site belongs to you. We don't hold your digital assets hostage.",
  },
];

export const Route = createFileRoute("/faq")({
  head: () => ({
    ...pageMeta({
      path: "/faq/",
      title: `Frequently Asked Questions — SEO & AI Search | ${siteConfig.name}`,
      description: `Common questions about SEO pricing, timelines, AI search (GEO/AEO), and our engineering-led growth process in Austin & Texas.`,
    }),
    scripts: ldScripts(
      breadcrumbLd([
        { name: "Home", path: "/" },
        { name: "FAQ", path: "/faq/" },
      ]),
      faqLd(CONSOLIDATED_FAQS),
    ),
  }),
  component: FAQPage,
});

function FAQPage() {
  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <section className="container-page pt-24 pb-16">
          <SectionHeading
            eyebrow="Knowledge Base"
            title="Common questions about search growth."
            intro="Everything you need to know about how we build organic revenue channels in the age of AI search."
          />
        </section>

        <section className="container-page pb-32">
          <div className="grid gap-8 md:grid-cols-2">
            {CONSOLIDATED_FAQS.map((f) => (
              <div key={f.q} className="rounded-3xl border border-border p-8 bg-card/20">
                <dt className="font-display text-xl leading-tight tracking-tight">{f.q}</dt>
                <dd className="mt-4 text-muted-foreground leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </div>
          
          <div className="mt-20 rounded-3xl border border-primary/20 bg-primary/5 p-8 md:p-12 text-center">
            <h2 className="font-display text-3xl">Still have questions?</h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
              Book a free 30-minute strategy call. We'll audit your current footprint and answer anything specific to your business.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact/"
                search={{ location: "", service: "" }}
                className="rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground shadow-sm hover:-translate-y-0.5 transition-transform"
              >
                Book a Strategy Call
              </Link>
              <Link
                to="/pricing/"
                className="rounded-full border border-border-strong px-8 py-3 text-sm font-medium hover:border-primary transition-colors"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
